import { type Row, type Table } from "@tanstack/react-table";
import { type ChangeEvent, type FC, useRef } from "react";

import { InputTextBox } from "../../InputTextBox";
import { useEditableValue } from "../useEditableValue";
import { TableCellValueRowDetail } from "./TableCellValueRowDetail";
import { type TableCellValueCommonProps } from "./types";

export interface TableCellValueMultilineTextProps extends TableCellValueCommonProps {
	table: Table<unknown>;
	row: Row<unknown>;
	header?: string;
	columnID: string;
	renderAsRowDetail?: boolean;
}

export const TableCellValueMultilineText: FC<TableCellValueMultilineTextProps> = ({
	table,
	row,
	value: inputValue,
	defaultValue,
	onChange,
	columnID,
	renderAsRowDetail,
	isEditable,
}) => {
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const { value, isControlled, handleChange, handleBlur, handleKeyDown } =
		useEditableValue<string>("text", isEditable, inputValue, defaultValue, onChange, inputRef);

	const staticContent =
		value && value?.length > 0 ? (
			<div className="w-full overflow-x-hidden whitespace-pre-wrap text-primary">{value}</div>
		) : (
			<div className="italic text-secondary">No content.</div>
		);

	const content = (
		<div className="w-full md:w-[952px]">
			{isEditable ? (
				<InputTextBox
					fullWidth
					ref={inputRef}
					value={isControlled ? value : undefined}
					defaultValue={value}
					onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange(e.target.value)}
					onBlur={renderAsRowDetail ? handleBlur : undefined}
					onKeyDown={renderAsRowDetail ? handleKeyDown : undefined}
					minRows={19}
					maxRows={19}
					size="sm"
				/>
			) : (
				staticContent
			)}
		</div>
	);

	if (renderAsRowDetail) {
		return isEditable ? content : staticContent;
	}

	return (
		<TableCellValueRowDetail
			rowType="Multiline text"
			value={value}
			table={table}
			columnId={columnID}
			row={row}
		/>
	);
};
